export enum ReturnsSortingMode {
  SERVICE_LEVEL = "service_level",
  PRIORITY = "priority",
}

export const returnsSortingMode = Object.values(ReturnsSortingMode);

export enum RedoManagedReturnsSortingMode {
  DEFAULT = "default",
  HIGH_PRIORITY = "high_priority",
}

export const redoManagedReturnsSortingMode = Object.values(
  RedoManagedReturnsSortingMode,
);

export const serviceLevelSort = [
  "verification",
  "grading",
  "quarantine",
] as const;
export type ServiceLevelSort = (typeof serviceLevelSort)[number];

export const prioritySort = [
  "redo_owned_no_refulfillment",
  "redo_owned_refulfillment",
  "high_priority",
  "low_priority",
  "quarantine",
] as const;
export type PrioritySort = (typeof prioritySort)[number];

export type ActiveReturnSort = ServiceLevelSort | PrioritySort;

export const sortingReason = [
  "quarantine",
  "redo_owned",
  "returns_center_refulfillment_enabled",
  "auto_processing_disabled",
  "auto_processing_ineligible",
  "high_priority_merchant",
  "default_low_priority",
] as const;
export type SortingReason = (typeof sortingReason)[number];

export const defaultReturnsSortingMode = ReturnsSortingMode.SERVICE_LEVEL;
export const defaultRedoManagedReturnsSortingMode =
  RedoManagedReturnsSortingMode.DEFAULT;

export function isReturnsSortingMode(
  value: string | null | undefined,
): value is ReturnsSortingMode {
  return returnsSortingMode.includes(value as ReturnsSortingMode);
}

export function isRedoManagedReturnsSortingMode(
  value: string | null | undefined,
): value is RedoManagedReturnsSortingMode {
  return redoManagedReturnsSortingMode.includes(
    value as RedoManagedReturnsSortingMode,
  );
}

export const returnsSortingModeLabels: Record<ReturnsSortingMode, string> = {
  [ReturnsSortingMode.SERVICE_LEVEL]: "Service level",
  [ReturnsSortingMode.PRIORITY]: "Priority",
};

export const redoManagedReturnsSortingModeLabels: Record<
  RedoManagedReturnsSortingMode,
  string
> = {
  [RedoManagedReturnsSortingMode.DEFAULT]: "Default",
  [RedoManagedReturnsSortingMode.HIGH_PRIORITY]: "Priority",
};

export const serviceLevelSortDestinations: Record<ServiceLevelSort, string> = {
  verification: "Verification",
  grading: "Grading",
  quarantine: "Quarantine",
};

export const prioritySortDestinations: Record<PrioritySort, string> = {
  redo_owned_no_refulfillment: "Redo Owned (Liquidate)",
  redo_owned_refulfillment: "Redo Owned (Refulfill)",
  high_priority: "Priority",
  low_priority: "Standard",
  quarantine: "Quarantine",
};

export const sortingReasonLabels: Record<SortingReason, string> = {
  quarantine: "Quarantine",
  redo_owned: "Redo-owned",
  returns_center_refulfillment_enabled: "Refulfillment enabled",
  auto_processing_disabled: "Auto-processing disabled",
  auto_processing_ineligible: "Auto-processing ineligible",
  high_priority_merchant: "Priority merchant",
  default_low_priority: "Standard",
};

export function getDestinationForServiceLevelSort(
  level: ServiceLevelSort,
): string {
  return serviceLevelSortDestinations[level];
}

export function getDestinationForPrioritySort(priority: PrioritySort): string {
  return prioritySortDestinations[priority];
}

export function getDestinationForActiveSort(input: {
  sortingMode: ReturnsSortingMode;
  serviceLevel: ServiceLevelSort;
  priority: PrioritySort;
}): string {
  if (input.priority === "quarantine" || input.serviceLevel === "quarantine") {
    return getDestinationForPrioritySort("quarantine");
  }
  return input.sortingMode === ReturnsSortingMode.PRIORITY
    ? getDestinationForPrioritySort(input.priority)
    : getDestinationForServiceLevelSort(input.serviceLevel);
}
